import Cookies from 'universal-cookie';

/**
 * Cookie utility functions for managing browser cookies
 * Uses universal-cookie library for cross-browser compatibility
 */

const cookies = new Cookies();

/**
 * Get a cookie value by name
 * @param {string} name - The name of the cookie to retrieve
 * @returns {any} The cookie value or undefined if not found
 */
export const getCookie = (name) => {
  return cookies.get(name);
};

/**
 * Set a cookie with the given name, value, and options
 * @param {string} name - The name of the cookie
 * @param {any} value - The value to store in the cookie
 * @param {object} options - Cookie options (path, domain, expires, secure, sameSite)
 */
export const setCookie = (name, value, options = {}) => {
  const defaultOptions = {
    path: '/',
    ...options
  };
  
  cookies.set(name, value, defaultOptions);
};

/**
 * Remove a cookie by name
 * @param {string} name - The name of the cookie to remove
 * @param {object} options - Cookie options (path, domain)
 */
export const removeCookie = (name, options = {}) => {
  const defaultOptions = {
    path: '/',
    ...options
  };
  
  cookies.remove(name, defaultOptions);
};

/**
 * Get the default plan type from cookie
 * This cookie can be set from the main domain (followr.ai) and read from subdomains (app.followr.ai)
 * @returns {string|undefined} 'ai_studio', 'social', or undefined if not set
 */
export const getDefaultPlanTypeCookie = () => {
  const planType = getCookie('followr_default_plan_type');
  
  // Validate the plan type to ensure it's a valid value
  if (planType === 'ai_studio' || planType === 'social') {
    return planType;
  }
  
  return undefined;
};

/**
 * Set the default plan type cookie
 * Should be called from the main domain with domain=".followr.ai" to allow subdomain access
 * @param {string} planType - Either 'ai_studio' or 'social'
 * @param {string} domain - The domain to set the cookie for (e.g., '.followr.ai')
 */
export const setDefaultPlanTypeCookie = (planType, domain) => {
  if (planType !== 'ai_studio' && planType !== 'social') {
    console.warn('Invalid plan type. Must be "ai_studio" or "social"');
    return;
  }
  
  setCookie('followr_default_plan_type', planType, {
    domain: domain || '.followr.ai',
    expires: new Date(new Date().getTime() + 365 * 24 * 60 * 60 * 1000), // 1 year
    secure: true,
    sameSite: 'lax' // 'lax' allows the cookie to be sent on top-level navigation
  });
};

/**
 * Initialize the default plan type cookie based on referrer
 * This should be called once on app load to automatically set the plan type
 * based on where the user came from (followr.ai vs followr.studio)
 */
export const initializePlanTypeCookie = () => {
  // Check if cookie is already set
  const existingPlanType = getDefaultPlanTypeCookie();
  if (existingPlanType) {
    return; // Cookie already set, don't override
  }
  
  // Check referrer
  const referrer = document.referrer;
  if (referrer && referrer.includes('followr.studio')) {
    setDefaultPlanTypeCookie('ai_studio', '.followr.ai');
  }
  // If no referrer or other referrer, leave unset (defaults to 'social' in subscription view)
};


